/*
 * Copyright (c) Harness Inc.
 *
 * This source code is licensed under the Apache 2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { ReactElement } from 'react'
import cx from 'classnames'
import { Position } from '@blueprintjs/core'
import { defaultTo } from 'lodash-es'
import { MultiSelect as BPMultiSelect, IMultiSelectProps, IItemRendererProps } from '@blueprintjs/select'

import css from './MultiSelect.css'
import { Button } from '../../components/Button/Button'
import { Text } from '../../components/Text/Text'
import { Icon } from '@harnessio/icons'
import { Utils } from '../../core/Utils'

export interface MultiSelectOption {
  label: string
  value: string | number | symbol
  disabled?: boolean
}

type Props = IMultiSelectProps<MultiSelectOption>

const Loading = Symbol('loading')

export interface MultiSelectProps
  extends Omit<
    Props,
    | 'popoverProps'
    | 'selectedItems'
    | 'itemRenderer'
    | 'onItemSelect'
    | 'items'
    | 'tagRenderer'
    | 'activeItem'
    | 'onActiveItemChange'
  > {
  itemRender?: Props['itemRenderer']
  onChange?(opts: MultiSelectOption[]): void
  value?: MultiSelectOption[]
  items: Props['items'] | (() => Promise<Props['items']>)
  tagRenderer?: Props['tagRenderer']
  createNewItemFromQuery?: Props['createNewItemFromQuery']
  allowCreatingNewItems?: boolean
  name?: string
  onPopoverClose?: (node: HTMLElement) => void
  disabled?: boolean
  usePortal?: boolean
  popoverClassName?: string
  allowCommaSeparatedList?: boolean
}

export function NoMatch(): React.ReactElement {
  return <li className={cx(css.menuItem, css.disabled)}>No matching results found</li>
}

export function MultiSelect(props: MultiSelectProps): React.ReactElement {
  const {
    onChange,
    value,
    items: _items,
    onPopoverClose,
    disabled,
    popoverClassName,
    allowCommaSeparatedList,
    ...rest
  } = props
  const [query, setQuery] = React.useState(props.query || '')
  const [loading, setLoading] = React.useState(false)
  const [items, setItems] = React.useState<MultiSelectOption[]>(Array.isArray(_items) ? _items : [])
  const [selectedItems, setSelectedItems] = React.useState<MultiSelectOption[]>(Array.isArray(value) ? value : [])
  const valuesMap: Record<string, boolean> = React.useMemo(
    () => (value || []).reduce((acc, current) => ({ ...acc, [current.label]: !!current.disabled }), {}),
    [value]
  )

  function handleItemSelect(item: MultiSelectOption) {
    if (item.value === Loading || item.disabled) {
      return
    }

    if (typeof onChange === 'function') {
      const index = selectedItems.findIndex(opt => opt.value === item.value)

      if (index < 0) {
        if (allowCommaSeparatedList && typeof item.value === 'string' && item.value.indexOf(',') > -1) {
          const values: MultiSelectOption[] = item.value
            .split(',')
            .filter(i => !!i)
            .map(value => ({ label: value, value: value.trim() }))
          onChange(selectedItems.concat(values))
        } else {
          onChange(selectedItems.concat(item))
        }
        setQuery('')
      } else {
        onChange(selectedItems.filter((_, i) => i !== index))
      }
    }
  }

  function handleQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value } = e.target
    setQuery(value)
  }

  React.useEffect(() => {
    if (Array.isArray(_items)) {
      setItems(_items)
    } else if (typeof _items === 'function') {
      setLoading(true)
      const promise = _items()

      if (typeof promise.then === 'function') {
        promise.then(results => {
          setItems(results)
          setLoading(false)
        })
      } else {
        setLoading(false)
      }
    }
  }, [_items])

  React.useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedItems(value)
    }
  }, [value])

  React.useEffect(() => {
    setQuery(props.query || '')
  }, [props.query])

  function itemRenderer(item: MultiSelectOption, rendererProps: IItemRendererProps): JSX.Element | null {
    if (!rendererProps.modifiers.matchesPredicate) {
      return null
    }

    if (item.value === Loading) {
      return (
        <li key="loading" className={cx(css.menuItem, css.loading)}>
          Loading results...
        </li>
      )
    }

    return (
      <li
        key={item.value.toString()}
        className={cx(css.menuItem, {
          [css.active]: rendererProps.modifiers.active,
          [css.disabled]: rendererProps.modifiers.disabled || item.disabled
        })}
        onClick={rendererProps.handleClick}>
        <input
          className={css.checkbox}
          type="checkbox"
          value={item.value.toString()}
          checked={value && value.findIndex(val => val.value === item.value) > -1}
          disabled={item.disabled}
          readOnly
        />
        <Text className={css.menuItemLabel} lineClamp={1}>
          {item.label}
        </Text>
      </li>
    )
  }

  function createNewItemFromQuery(query: string) {
    return { label: query, value: query }
  }

  function tagRenderer(item: MultiSelectOption) {
    return item.label
  }

  function createNewItemRenderer(query: string, _active: boolean, handleClick: any) {
    if (loading) {
      return (
        <li key="loading" className={cx(css.menuItem, css.loading)}>
          Loading results...
        </li>
      )
    }

    if (
      !loading &&
      props.allowCreatingNewItems &&
      items.filter(item => item.label.toString().toLowerCase() === query.toLowerCase()).length === 0
    )
      return (
        <React.Fragment>
          <Button intent="primary" minimal text={query} icon="plus" onClick={handleClick} />
          <span className="icon-container">
            <Icon id="icon-styled-props" name="info-sign" size={16} color="grey400" padding="small" />
          </span>
        </React.Fragment>
      )
  }

  return (
    <BPMultiSelect
      itemRenderer={props.itemRender || itemRenderer}
      createNewItemFromQuery={props.createNewItemFromQuery || createNewItemFromQuery}
      createNewItemRenderer={props.createNewItemRenderer || createNewItemRenderer}
      tagRenderer={props.tagRenderer || tagRenderer}
      itemsEqual={(a, b) => a.value === b.value}
      itemListPredicate={(query, items) =>
        items.filter(item => item.label.toString().toLowerCase().includes(query.toLowerCase()))
      }
      {...rest}
      tagInputProps={{
        disabled,
        placeholder: Utils.getSelectComponentPlaceholder(
          defaultTo(rest?.placeholder, rest?.tagInputProps?.inputProps?.placeholder)
        ),
        tagProps: value => {
          return {
            className: cx(css.tag, {
              [css.tagDisabled]: typeof value === 'string' && valuesMap[value]
            })
          }
        },

        onRemove(value: React.ReactNode) {
          const option = selectedItems.find(opt => {
            if (typeof value === 'string') return opt.label === value
            else return opt.value === (value as ReactElement).key
          })
          if (option) {
            handleItemSelect(option)
          }
        },
        ...props.tagInputProps,
        inputProps: {
          ...props.tagInputProps?.inputProps,
          onChange: handleQueryChange,
          name: props.name,
          disabled
        }
      }}
      items={loading ? [{ label: 'Loading...', value: Loading }] : items}
      selectedItems={value}
      onItemSelect={handleItemSelect}
      onItemsPaste={items => {
        if (typeof onChange === 'function' && allowCommaSeparatedList) {
          onChange(selectedItems.concat(items))
        }
      }}
      query={query}
      noResults={<NoMatch />}
      popoverProps={{
        targetTagName: 'div',
        wrapperTagName: 'div',
        fill: true,
        usePortal: !!props.usePortal,
        minimal: true,
        position: Position.BOTTOM_LEFT,
        className: css.main,
        popoverClassName: cx(css.popover, popoverClassName),
        onClosed: onPopoverClose,
        modifiers: {
          preventOverflow: {
            // This is required to always attach the portal to the start of the reference instead of the middle
            escapeWithReference: !!props.usePortal
          },
          offset: {
            // This is required to offset the portal after it is attached to the reference.
            // By default the portal is positioned at top: 0, left:0 wrt it's reference
            offset: props.usePortal ? '1 2' : 0
          }
        }
      }}
    />
  )
}
